import emailjs from "@emailjs/browser"

const EMAILJS_CONFIG = {
  serviceId: (process.env.REACT_APP_EMAILJS_SERVICE_ID || "").trim(),
  publicKey: (process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "").trim(),
  adminTemplateId: (
    process.env.REACT_APP_EMAILJS_ORDER_ADMIN_TEMPLATE_ID || ""
  ).trim(),
  clientTemplateId: (
    process.env.REACT_APP_EMAILJS_ORDER_CLIENT_TEMPLATE_ID || ""
  ).trim(),
  ownerEmail: (process.env.REACT_APP_ORDER_OWNER_EMAIL || "").trim(),
}

const getMissingConfigKeys = ({ requireOwnerEmail }) => {
  const missingKeys = []

  if (!EMAILJS_CONFIG.serviceId) {
    missingKeys.push("REACT_APP_EMAILJS_SERVICE_ID")
  }

  if (!EMAILJS_CONFIG.publicKey) {
    missingKeys.push("REACT_APP_EMAILJS_PUBLIC_KEY")
  }

  if (requireOwnerEmail && !EMAILJS_CONFIG.ownerEmail) {
    missingKeys.push("REACT_APP_ORDER_OWNER_EMAIL")
  }

  return missingKeys
}

const extractEmailJsErrorMessage = (errorReason) => {
  if (!errorReason) {
    return "Unknown EmailJS error"
  }

  if (typeof errorReason === "string") {
    return errorReason.trim() || "Unknown EmailJS error"
  }

  if (typeof errorReason.status === "number") {
    const statusText =
      typeof errorReason.text === "string" && errorReason.text.trim()
        ? errorReason.text.trim()
        : "Unknown EmailJS error"

    return `HTTP ${errorReason.status}: ${statusText}`
  }

  if (typeof errorReason.text === "string" && errorReason.text.trim()) {
    return errorReason.text
  }

  if (typeof errorReason.message === "string" && errorReason.message.trim()) {
    return errorReason.message
  }

  try {
    return JSON.stringify(errorReason)
  } catch {
    return "Unable to parse EmailJS error"
  }
}

const buildRecipientTemplateParams = ({ baseParams, recipientEmail }) => {
  const normalizedRecipientEmail = (recipientEmail || "").trim()

  return {
    ...baseParams,
    // Support templates using either {{to_email}} or {{customer_email}} as recipient
    to_email: normalizedRecipientEmail,
    customer_email: normalizedRecipientEmail,
  }
}

const getOrderSummaryExcerpt = (summary) => {
  const normalizedSummary = (summary || "").trim()
  if (normalizedSummary.length <= 400) {
    return normalizedSummary
  }

  return `${normalizedSummary.slice(0, 400)}...`
}

const getCloudinaryDownloadUrl = (sourceUrl) => {
  const normalizedSourceUrl = (sourceUrl || "").trim()

  if (!normalizedSourceUrl) {
    return ""
  }

  if (normalizedSourceUrl.includes("/upload/fl_attachment/")) {
    return normalizedSourceUrl
  }

  // Convert Cloudinary delivery URL into forced-download URL.
  // Example: .../upload/... -> .../upload/fl_attachment/...
  if (
    normalizedSourceUrl.includes("/res.cloudinary.com/") &&
    normalizedSourceUrl.includes("/upload/")
  ) {
    return normalizedSourceUrl.replace("/upload/", "/upload/fl_attachment/")
  }

  return normalizedSourceUrl
}

const buildTemplateParams = ({ orderId, service, customer }) => {
  const requestQuoteText = customer.requestQuote ? "Oui" : "Non"
  const ownerEmailValue = EMAILJS_CONFIG.ownerEmail || "Non renseignée"
  const fullSummary = (customer.summary || "").trim()
  const summaryForEmail = fullSummary || "Non renseignée"
  const attachment = customer.attachment || null
  const attachmentPreviewUrl = (attachment?.downloadUrl || "").trim()
  const attachmentDownloadUrl = getCloudinaryDownloadUrl(attachmentPreviewUrl)
  const hasAttachment = Boolean(attachmentPreviewUrl)
  const attachmentName = (attachment?.name || "").trim()
  const attachmentDisplayName = attachmentName || "Document joint"
  const attachmentDisplay = hasAttachment
    ? `${attachmentDisplayName}: ${attachmentPreviewUrl}`
    : "Non renseignée"

  return {
    order_id: orderId,
    owner_email: ownerEmailValue,
    to_email: ownerEmailValue,
    customer_name: customer.fullName,
    customer_email: customer.email,
    customer_company: customer.company || "Non renseignée",
    service_id: service.id,
    service_title: service.title,
    service_highlight: service.highlight,
    request_quote: requestQuoteText,
    order_summary: summaryForEmail,
    // Keep this full for backward compatibility with templates already using order_summary_preview
    order_summary_preview: summaryForEmail,
    // Optional short excerpt if you want compact rendering in some templates
    order_summary_excerpt: getOrderSummaryExcerpt(summaryForEmail),
    has_attachment: hasAttachment ? "Oui" : "Non",
    attachment_name: hasAttachment ? attachmentDisplayName : "NA",
    attachment_url: hasAttachment ? attachmentPreviewUrl : "NA",
    attachment_display: attachmentDisplay,
    attachment_preview_url: hasAttachment ? attachmentPreviewUrl : "NA",
    attachment_download_url: hasAttachment ? attachmentDownloadUrl : "NA",
    attachment_type: attachment?.contentType || "",
    attachment_size_kb: attachment?.size
      ? Math.max(1, Math.round(attachment.size / 1024))
      : "",
  }
}

export const notifyServiceOrderByEmail = async ({
  orderId,
  service,
  customer,
}) => {
  if (!EMAILJS_CONFIG.adminTemplateId && !EMAILJS_CONFIG.clientTemplateId) {
    console.warn(
      "Service order notifications skipped: missing EmailJS order template IDs."
    )

    return {
      skipped: true,
      adminSent: false,
      clientSent: false,
      missingAdminTemplate: true,
      missingClientTemplate: true,
      configurationError: null,
      reason:
        "Missing EmailJS order template IDs. Configure REACT_APP_EMAILJS_ORDER_ADMIN_TEMPLATE_ID and REACT_APP_EMAILJS_ORDER_CLIENT_TEMPLATE_ID.",
    }
  }

  const missingConfigKeys = getMissingConfigKeys({
    requireOwnerEmail: Boolean(EMAILJS_CONFIG.adminTemplateId),
  })

  if (missingConfigKeys.length > 0) {
    return {
      skipped: true,
      adminSent: false,
      clientSent: false,
      missingAdminTemplate: !EMAILJS_CONFIG.adminTemplateId,
      missingClientTemplate: !EMAILJS_CONFIG.clientTemplateId,
      configurationError: `Missing configuration keys: ${missingConfigKeys.join(", ")}`,
      reason:
        "Email notifications are disabled due to incomplete configuration.",
    }
  }

  const templateParams = buildTemplateParams({ orderId, service, customer })

  const adminParams = buildRecipientTemplateParams({
    baseParams: templateParams,
    recipientEmail: EMAILJS_CONFIG.ownerEmail,
  })

  const clientParams = buildRecipientTemplateParams({
    baseParams: templateParams,
    recipientEmail: customer.email,
  })

  const adminSendPromise = EMAILJS_CONFIG.adminTemplateId
    ? emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.adminTemplateId,
        adminParams,
        EMAILJS_CONFIG.publicKey
      )
    : Promise.resolve("admin-template-missing")

  const clientSendPromise = EMAILJS_CONFIG.clientTemplateId
    ? emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.clientTemplateId,
        clientParams,
        EMAILJS_CONFIG.publicKey
      )
    : Promise.resolve("client-template-missing")

  const [adminResult, clientResult] = await Promise.allSettled([
    adminSendPromise,
    clientSendPromise,
  ])

  const adminSent =
    EMAILJS_CONFIG.adminTemplateId && adminResult.status === "fulfilled"
  const clientSent =
    EMAILJS_CONFIG.clientTemplateId && clientResult.status === "fulfilled"

  if (EMAILJS_CONFIG.adminTemplateId && adminResult.status === "rejected") {
    console.warn("Service order admin email failed", {
      serviceId: EMAILJS_CONFIG.serviceId,
      templateId: EMAILJS_CONFIG.adminTemplateId,
      reason: adminResult.reason,
    })
  }

  if (EMAILJS_CONFIG.clientTemplateId && clientResult.status === "rejected") {
    console.warn("Service order client email failed", {
      serviceId: EMAILJS_CONFIG.serviceId,
      templateId: EMAILJS_CONFIG.clientTemplateId,
      reason: clientResult.reason,
    })
  }

  return {
    skipped: false,
    adminSent,
    clientSent,
    missingAdminTemplate: !EMAILJS_CONFIG.adminTemplateId,
    missingClientTemplate: !EMAILJS_CONFIG.clientTemplateId,
    configurationError: null,
    adminError:
      EMAILJS_CONFIG.adminTemplateId && adminResult.status === "rejected"
        ? extractEmailJsErrorMessage(adminResult.reason)
        : null,
    clientError:
      EMAILJS_CONFIG.clientTemplateId && clientResult.status === "rejected"
        ? extractEmailJsErrorMessage(clientResult.reason)
        : null,
  }
}
